import React, { useEffect, useMemo, useRef } from 'react';
import { Bell, MessageSquare, LayoutDashboard, Users, Plus, Sparkles } from 'lucide-react';
import Portal from './ui/Portal';

export type HeaderPopoverType = 'notifications' | 'create';

interface HeaderPopoverProps {
  type: HeaderPopoverType;
  anchorRect: DOMRect;
  onClose: () => void;
}

const HeaderPopover: React.FC<HeaderPopoverProps> = ({ type, anchorRect, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (panelRef.current?.contains(target)) return;
      if (target.closest('[data-header-popover-trigger]')) return;
      onClose();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', handleClick);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const style = useMemo(() => {
    const width = type === 'notifications' ? 320 : 360;
    const top = Math.min(anchorRect.bottom + 12, window.innerHeight - 24 - 360);
    const left = Math.min(Math.max(16, anchorRect.right - width), window.innerWidth - width - 16);
    return { top, left, width };
  }, [anchorRect, type]);

  const notifications = [
    { id: 'notif-1', title: '5-story workflow shipped', detail: 'Studio team closed the sprint.', time: '2m ago' },
    { id: 'notif-2', title: 'Security review required', detail: 'Legal needs eyes on the contract.', time: '35m ago' },
    { id: 'notif-3', title: 'Weekly performance digest', detail: 'See engagement metrics for Nebula.', time: 'Today' }
  ];

  const createActions = [
    { id: 'create-1', title: 'New Blueprint', detail: 'Spin up a module canvas.', icon: LayoutDashboard },
    { id: 'create-2', title: 'Invite Member', detail: 'Send a workspace invite.', icon: Users },
    { id: 'create-3', title: 'Log Conversation', detail: 'Capture a customer note.', icon: MessageSquare },
    { id: 'create-4', title: 'Automation', detail: 'Kick off a scheduled flow.', icon: Sparkles }
  ];

  return (
    <Portal>
      <div className="fixed inset-0 z-[85] pointer-events-none">
        <div
          ref={panelRef}
          className="pointer-events-auto absolute rounded-3xl border border-border bg-card backdrop-blur-sm shadow-[0_18px_60px_rgba(2,6,23,0.55)] overflow-hidden"
          style={style}
        >
          {type === 'notifications' ? (
            <div className="flex flex-col h-[360px]">
              <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2 text-sm font-semibold">
                <Bell size={16} /> Notifications
              </div>
              <div className="relative flex-1 overflow-y-auto p-4 space-y-4">
                <div className="absolute left-5 top-4 bottom-4 w-px bg-border/40 pointer-events-none" aria-hidden="true" />
                {notifications.map(note => (
                  <div key={note.id} className="relative pl-6">
                    <span className="absolute left-0 top-2 w-2 h-2 rounded-full bg-primary" />
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">{note.time}</p>
                    <p className="text-sm font-semibold leading-snug">{note.title}</p>
                    <p className="text-xs text-foreground/70 mt-1">{note.detail}</p>
                  </div>
                ))}
              </div>
              <button className="m-4 mt-0 rounded-xl border border-border/60 py-2 text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors">
                View feed
              </button>
            </div>
          ) : (
            <div className="flex flex-col h-[360px]">
              <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2 text-sm font-semibold">
                <Plus size={16} /> Create
              </div>
              <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 gap-3">
                {createActions.map(action => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.id}
                      className="text-left p-4 rounded-2xl border border-border/60 hover:border-primary/40 hover:bg-accent/40 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-2xl bg-accent/50 flex items-center justify-center">
                          <Icon size={18} className="text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold leading-tight">{action.title}</p>
                          <p className="text-xs text-foreground/70">{action.detail}</p>
                        </div>
                      </div>
                      <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground/80">Quick action</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default HeaderPopover;
